import { DevTool } from '@hookform/devtools'
import { yupResolver } from '@hookform/resolvers/yup'
import { captureException } from '@sentry/react'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import * as Yup from 'yup'

import type { ReactElement } from 'react'
import type { IReplyToReviewsPayload, IReplyToReviewsResponse, IReview } from '~/types/review'

import CharacterCount from './CharacterCount'

import Button from '~/components/Buttons/Button'
import SelectInput from '~/components/Inputs/ReactHookForm/SelectInput'
import TextArea from '~/components/Inputs/ReactHookForm/TextArea'
import Modal from '~/components/Modal'
import { ModalBody, ModalFooter, ModalHeader } from '~/components/Modal/Layout'
import RequiredInformation from '~/components/RequiredInformation'
import CustomLongTextOption from '~/components/SelectOptions/CustomLongTextOption'
import Tabs from '~/components/Tabs'
import Truncate from '~/components/Truncate'
import { DEVICE_TYPE, FORM_ERRORS } from '~/const'
import { useReplyToReviewsMutation } from '~/queries/reviews'
import { useReviewsReplyMacrosQuery } from '~/queries/reviews-reply-macros'
import { reactSelectMenuPositionDef } from '~/utils/options'
import { RequiredYupOption } from '~/utils/yup'

import './styles.scss'
import './../styles.scss'

const MODAL_ID = 'reply-review-modal'

const ANDROID_REPLY_MAX_CHARACTERS = 350

enum TAB_TYPE {
  MACRO = 'macro',
  PERSONALIZED = 'personalized',
}

const tabsData = [
  { label: 'Personalized', name: TAB_TYPE.PERSONALIZED },
  { label: 'Macro', name: TAB_TYPE.MACRO },
]

interface FormValues {
  macro: IOption | null
  macroEdited: string
  macroName: IOption | null
  response: string
}

const hasAndroid = (reviews: IReview[]) => reviews.some(({ device_type }) => device_type === DEVICE_TYPE.ANDROID)

const contentValidationSchemaAndroid = Yup.string()
  .trim()
  .required(FORM_ERRORS.REQUIRED)
  .max(ANDROID_REPLY_MAX_CHARACTERS, `Your answer can't exceed ${ANDROID_REPLY_MAX_CHARACTERS} characters`)

const contentValidationSchemaIos = Yup.string().trim().required(FORM_ERRORS.REQUIRED)

interface ModalFormProps {
  onClose: VoidFunction
  onFailure: VoidFunction
  onSuccess: (response: IReplyToReviewsResponse) => void
  reviews: IReview[]
}

const ReplyModalForm = ({ onClose, onFailure, onSuccess, reviews }: ModalFormProps): ReactElement => {
  /** Local state */

  const [selectedTab, setSelectedTab] = useState(TAB_TYPE.PERSONALIZED)

  const defaultValues: FormValues = {
    macro: null,
    macroEdited: '',
    macroName: null,
    response: reviews.length === 1 ? reviews[0]?.response_openai : '',
  }

  const contentValidationSchema = hasAndroid(reviews) ? contentValidationSchemaAndroid : contentValidationSchemaIos

  const validationSchemas: Record<TAB_TYPE, Yup.ObjectSchema<any>> = {
    [TAB_TYPE.MACRO]: Yup.object().shape({
      macro: RequiredYupOption.nullable().notOneOf([null], FORM_ERRORS.REQUIRED),
      macroEdited: contentValidationSchema,
      macroName: RequiredYupOption.nullable().notOneOf([null], FORM_ERRORS.REQUIRED),
    }),
    [TAB_TYPE.PERSONALIZED]: Yup.object().shape({
      response: contentValidationSchema,
    }),
  }

  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
    register,
    reset,
    resetField,
    setValue,
    watch,
  } = useForm<FormValues>({
    defaultValues,
    resolver: yupResolver(validationSchemas[selectedTab]),
  })

  const macroEditedWatch = watch('macroEdited')

  const macroNameWatch = watch('macroName')

  const responseWatch = watch('response')

  /** Queries & Mutations */

  const { data: macros = [] } = useReviewsReplyMacrosQuery()

  const applicationsList = [...new Set(reviews.map(({ app_key, device_type }) => ({ app_key, device_type })))]

  const macrosFilteredByAppKey = macros.filter(
    ({ applications, is_all_apps }) =>
      is_all_apps ||
      applications?.some((app) =>
        applicationsList.some(({ app_key, device_type }) => app.app_key === app_key && app.device_type === device_type)
      )
  )

  const macroNameOptions = [...new Set(macrosFilteredByAppKey.map(({ name }) => name))].map((name) => ({
    label: name,
    value: name,
  }))

  const macroOptions =
    macrosFilteredByAppKey
      .filter(({ name }) => name === macroNameWatch?.label)
      .map(({ content }) => ({
        label: content,
        value: content,
      })) || []

  const { mutateAsync: replyToReviews } = useReplyToReviewsMutation()

  /** Handlers */

  const handleTabChanged = (value: TAB_TYPE) => setSelectedTab(value)

  const handleFormSubmit = async (values: FormValues) => {
    const payload: IReplyToReviewsPayload = {
      content: selectedTab === TAB_TYPE.PERSONALIZED ? values.response : values.macroEdited,
      review_ids: reviews.map(({ id }) => id),
    }

    try {
      const response = await replyToReviews(payload)
      onSuccess(response)
    } catch (e) {
      captureException(e)
      onFailure()
    }
  }

  /** Render */

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <ModalBody>
        {reviews.length === 1 ? (
          <Truncate className="review-reply-modal__review-content">{reviews[0].content_en}</Truncate>
        ) : null}
        <Tabs
          className="review-reply-modal-required-star"
          onClickTab={(value) => {
            handleTabChanged(value as TAB_TYPE)
            reset()
          }}
          tabsData={tabsData}
        />
        <RequiredInformation />
        {selectedTab === TAB_TYPE.PERSONALIZED ? (
          <>
            {reviews[0]?.response_openai_en ? (
              <div className="openai-translation">
                Translation:
                <div className="openai-translation__content">{reviews[0].response_openai_en}</div>
              </div>
            ) : null}
            <TextArea
              {...register('response')}
              error={errors.response?.message}
              label="Response"
              placeholder="Enter response"
              required
            />
            {hasAndroid(reviews) ? (
              <CharacterCount nbMaxCharacters={ANDROID_REPLY_MAX_CHARACTERS} text={responseWatch} />
            ) : null}
          </>
        ) : (
          <div className="general-fieldset">
            <Controller
              control={control}
              name="macroName"
              render={({ field }) => (
                <SelectInput
                  {...field}
                  {...reactSelectMenuPositionDef(MODAL_ID)}
                  error={errors.macroName?.message}
                  label="Macro name"
                  options={macroNameOptions}
                  placeholder="Select a macro name"
                  required
                />
              )}
            />
            <Controller
              control={control}
              name="macro"
              render={({ field }) => (
                <SelectInput
                  {...field}
                  {...reactSelectMenuPositionDef(MODAL_ID)}
                  components={{ Option: CustomLongTextOption }}
                  disabled={!macroNameWatch}
                  error={errors.macro?.message}
                  label="Macro"
                  onChange={(option) => {
                    field.onChange(option)
                    const textValue = (option as IOption | null)?.value
                    textValue ? setValue('macroEdited', textValue) : resetField('macroEdited')
                  }}
                  options={macroOptions}
                  placeholder={!macroNameWatch ? 'Select a macro name first' : 'Select a macro'}
                  required
                />
              )}
            />
            <div>
              <TextArea
                {...register('macroEdited')}
                error={errors.macroEdited?.message}
                label="Edit macro"
                placeholder="Enter text"
                required
              />
              {hasAndroid(reviews) ? (
                <CharacterCount nbMaxCharacters={ANDROID_REPLY_MAX_CHARACTERS} text={macroEditedWatch} />
              ) : null}
            </div>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button disabled={isSubmitting} ghost onClick={onClose}>
          Cancel
        </Button>
        <Button disabled={isSubmitting} type="submit">
          {isSubmitting ? 'Loading...' : 'Reply'}
        </Button>
      </ModalFooter>
      <DevTool control={control} />
    </form>
  )
}

interface ModalProps extends Omit<ModalFormProps, 'reviews'> {
  isOpen: boolean
  reviews?: IReview[]
}

const ReplyModal = ({ isOpen, onClose, onFailure, onSuccess, reviews }: ModalProps): ReactElement => {
  return (
    <Modal className="review-reply-modal" id={MODAL_ID} isOpen={isOpen} onCloseModal={onClose}>
      <ModalHeader>
        Reply to Review {reviews ? `(${reviews.length} review${reviews.length > 1 ? 's' : ''} selected)` : ''}
      </ModalHeader>
      {reviews ? (
        <ReplyModalForm onClose={onClose} onFailure={onFailure} onSuccess={onSuccess} reviews={reviews} />
      ) : null}
    </Modal>
  )
}

export default ReplyModal
